import type { Dimension, ScoreVector } from './dimensions';

type PrototypeValues = [number, number, number, number, number, number, number, number, number, number];

export type RegularType = {
  typeId: string;
  zhShare: string;
  zhDetail: string;
  enShare: string;
  enDetail: string;
  core: string;
  vector: ScoreVector;
};

export type HiddenType = {
  typeId: string;
  zhShare: string;
  zhDetail: string;
  enShare: string;
  enDetail: string;
  trigger: string;
  override: boolean;
};

export type PersonalityType = RegularType | HiddenType;

export const matchWeights: Record<Dimension, number> = {
  directness: 1.1,
  avoidance: 1.0,
  conflictHeat: 1.1,
  socialControl: 0.9,
  emotionalVolatility: 1.0,
  repairIntent: 1.15,
  boundaryFirmness: 1.0,
  memeDefense: 0.85,
  impulsiveness: 1.0,
  chaosAppetite: 0.9
};

export function vector(values: PrototypeValues): ScoreVector {
  const [
    directness,
    avoidance,
    conflictHeat,
    socialControl,
    emotionalVolatility,
    repairIntent,
    boundaryFirmness,
    memeDefense,
    impulsiveness,
    chaosAppetite
  ] = values;

  return {
    directness,
    avoidance,
    conflictHeat,
    socialControl,
    emotionalVolatility,
    repairIntent,
    boundaryFirmness,
    memeDefense,
    impulsiveness,
    chaosAppetite
  };
}

export const regularTypes: RegularType[] = [
  {
    typeId: 'clear_boundary_calm_repairer',
    zhShare: '边界侠',
    zhDetail: '温和边界修复者',
    enShare: 'Boundary Keeper',
    enDetail: 'Calm Boundary Repairer',
    core: '说话清楚，不爱拉扯；能拒绝，也愿意把关系补好。',
    vector: vector([78, 22, 30, 42, 28, 82, 86, 36, 24, 20])
  },
  {
    typeId: 'joke_shield_deflector',
    zhShare: '梗盾',
    zhDetail: '玩梗防御大师',
    enShare: 'Meme Shield',
    enDetail: 'Joke-Based Deflector',
    core: '尴尬、冲突、深情时刻，第一反应都是甩个梗保命。',
    vector: vector([42, 62, 24, 48, 36, 46, 38, 92, 44, 50])
  },
  {
    typeId: 'impulsive_runner',
    zhShare: '先溜了',
    zhDetail: '冲动撤退型',
    enShare: 'Quick Exit',
    enDetail: 'Impulsive Withdrawer',
    core: '压力一上来就想消失，冷静后才开始复盘。',
    vector: vector([28, 88, 42, 20, 70, 44, 36, 40, 82, 24])
  },
  {
    typeId: 'soft_escape_peacemaker',
    zhShare: '云躲躲',
    zhDetail: '温柔回避和平派',
    enShare: 'Soft Evader',
    enDetail: 'Gentle Conflict Avoider',
    core: '气氛不对就先撤，宁愿自己消化，也不想把场面弄僵。',
    vector: vector([24, 86, 18, 28, 42, 64, 34, 48, 22, 18])
  },
  {
    typeId: 'hot_take_spark',
    zhShare: '火花嘴',
    zhDetail: '即兴开火表达者',
    enShare: 'Hot Take',
    enDetail: 'Impulsive Direct Speaker',
    core: '反应快、嘴也快；不是想伤人，但常常先输出后补丁。',
    vector: vector([86, 14, 76, 34, 62, 48, 58, 52, 84, 56])
  },
  {
    typeId: 'group_vibe_manager',
    zhShare: '场控王',
    zhDetail: '群聊气氛调度员',
    enShare: 'Vibe Manager',
    enDetail: 'Social Atmosphere Controller',
    core: '擅长看场、接话、转移火力，像群聊里的隐形主持人。',
    vector: vector([56, 36, 32, 88, 34, 66, 50, 68, 38, 42])
  },
  {
    typeId: 'dramatic_reactor_soft_heart',
    zhShare: '戏很多',
    zhDetail: '高反应柔软派',
    enShare: 'Big Feelings',
    enDetail: 'Dramatic Soft Reactor',
    core: '情绪来得快、表达也满；吵归吵，心里其实很在乎。',
    vector: vector([64, 26, 72, 38, 88, 70, 46, 54, 66, 48])
  },
  {
    typeId: 'chill_observer_low_heat',
    zhShare: '淡人',
    zhDetail: '低波动旁观型',
    enShare: 'Chill Watcher',
    enDetail: 'Low-Heat Observer',
    core: '不太上头，也不急着站队；喜欢先看完这集再说。',
    vector: vector([40, 58, 12, 24, 16, 42, 44, 40, 14, 22])
  },
  {
    typeId: 'chaos_enjoyer_playful_instigator',
    zhShare: '乐子人',
    zhDetail: '轻度混沌煽风者',
    enShare: 'Chaos Fan',
    enDetail: 'Playful Instigator',
    core: '不一定想赢，但很想看剧情升级；擅长给场面加一点燃料。',
    vector: vector([58, 20, 62, 56, 46, 28, 38, 76, 70, 92])
  },
  {
    typeId: 'responsible_apologizer',
    zhShare: '补丁王',
    zhDetail: '高修复责任型',
    enShare: 'Patch Maker',
    enDetail: 'Responsible Repair Seeker',
    core: '一旦发现关系有裂缝，就会主动解释、道歉、补救。',
    vector: vector([60, 24, 28, 46, 36, 94, 54, 30, 26, 18])
  },
  {
    typeId: 'silent_boundary_wall',
    zhShare: '静音墙',
    zhDetail: '沉默边界守门人',
    enShare: 'Silent Wall',
    enDetail: 'Quiet Boundary Holder',
    core: '不爱解释太多，但底线很清楚；越界了就直接静音处理。',
    vector: vector([34, 74, 20, 30, 24, 34, 90, 28, 18, 16])
  },
  {
    typeId: 'strategic_peacemaker',
    zhShare: '和事佬',
    zhDetail: '策略型降温协调者',
    enShare: 'Peace Broker',
    enDetail: 'Strategic De-Escalator',
    core: '能听懂各方情绪，也知道什么时候递台阶最有效。',
    vector: vector([52, 40, 18, 76, 26, 86, 48, 42, 20, 14])
  },
  {
    typeId: 'blunt_truth_teller',
    zhShare: '直球怪',
    zhDetail: '高直率低包装表达者',
    enShare: 'Blunt Truth',
    enDetail: 'Direct No-Frills Speaker',
    core: '不喜欢绕弯，能一句说清就不写小作文。',
    vector: vector([94, 10, 48, 30, 32, 40, 72, 24, 38, 26])
  },
  {
    typeId: 'people_pleasing_buffer',
    zhShare: '软垫',
    zhDetail: '高忍耐关系缓冲垫',
    enShare: 'Soft Buffer',
    enDetail: 'People-Pleasing Cushion',
    core: '会优先照顾别人感受，常把自己的不舒服往后放。',
    vector: vector([20, 78, 16, 44, 46, 78, 20, 36, 18, 12])
  },
  {
    typeId: 'cool_operator_social_chess',
    zhShare: '冷场控',
    zhDetail: '冷静社交棋手',
    enShare: 'Cool Operator',
    enDetail: 'Controlled Social Strategist',
    core: '情绪不外露，场面却在心里排兵布阵。',
    vector: vector([58, 28, 22, 92, 14, 46, 68, 34, 16, 30])
  },
  {
    typeId: 'overexplaining_repairer',
    zhShare: '小作文',
    zhDetail: '过度解释修复型',
    enShare: 'Essay Mode',
    enDetail: 'Overexplaining Repair Seeker',
    core: '怕误会扩大，于是解释越写越长，越写越真诚。',
    vector: vector([50, 52, 34, 38, 76, 90, 30, 26, 42, 16])
  },
  {
    typeId: 'playful_firefighter',
    zhShare: '灭火梗',
    zhDetail: '玩梗降温修复者',
    enShare: 'Joke Medic',
    enDetail: 'Meme-Based De-Escalator',
    core: '不爱正面煽情，但会用梗把台阶递得很自然。',
    vector: vector([46, 44, 20, 64, 30, 76, 42, 88, 34, 36])
  },
  {
    typeId: 'boundary_fighter',
    zhShare: '硬边界',
    zhDetail: '高边界正面交涉者',
    enShare: 'Line Defender',
    enDetail: 'Righteous Boundary Fighter',
    core: '不能接受被糊弄或越界，必要时会正面开麦。',
    vector: vector([88, 12, 78, 40, 48, 42, 92, 30, 52, 38])
  },
  {
    typeId: 'mellow_compromiser',
    zhShare: '好说',
    zhDetail: '低冲突弹性妥协派',
    enShare: 'Easy Mode',
    enDetail: 'Mellow Compromiser',
    core: '不太计较，能过就过；关系顺滑比赢一局更重要。',
    vector: vector([38, 54, 14, 34, 22, 62, 28, 34, 18, 12])
  },
  {
    typeId: 'chaotic_good_helper',
    zhShare: '热心乱',
    zhDetail: '混沌热心救场者',
    enShare: 'Chaotic Helper',
    enDetail: 'Well-Meaning Chaos Fixer',
    core: '很想帮忙，也确实会帮；只是过程可能把剧情变复杂。',
    vector: vector([66, 18, 58, 62, 58, 78, 44, 64, 74, 82])
  },
  {
    typeId: 'private_processor',
    zhShare: '内耗机',
    zhDetail: '私下反复加工型',
    enShare: 'Inner Loop',
    enDetail: 'Private Emotional Processor',
    core: '表面没事，脑内已经开了三轮复盘会议。',
    vector: vector([26, 76, 24, 32, 72, 58, 34, 32, 20, 14])
  },
  {
    typeId: 'clean_cut_decider',
    zhShare: '断舍离',
    zhDetail: '果断切换关系型',
    enShare: 'Clean Cut',
    enDetail: 'Decisive Relationship Editor',
    core: '给机会，但不无限给；确认不合适后会快速抽离。',
    vector: vector([72, 46, 34, 48, 24, 30, 94, 22, 38, 20])
  },
  {
    typeId: 'public_joker_private_serious',
    zhShare: '表面梗',
    zhDetail: '外梗内认真型',
    enShare: 'Secretly Serious',
    enDetail: 'Public Joker Private Thinker',
    core: '台面上全是梗，私下其实把每句话都认真想过。',
    vector: vector([44, 56, 26, 52, 54, 64, 40, 90, 36, 40])
  },
  {
    typeId: 'storm_then_sorry',
    zhShare: '炸完补',
    zhDetail: '高热度事后修复型',
    enShare: 'Storm Repair',
    enDetail: 'Explosive Then Apologetic',
    core: '当下容易上头，但事后也真会低头补救。',
    vector: vector([78, 12, 88, 28, 86, 84, 50, 42, 76, 44])
  }
];

export const hiddenTypes: HiddenType[] = [
  {
    typeId: 'hidden_screenshot_archivist',
    zhShare: '截图党',
    zhDetail: '证据留存型旁观者',
    enShare: 'Screenshot Sage',
    enDetail: 'Evidence-Collecting Observer',
    trigger: 'Multiple screenshot/save-evidence events; `avoidance >= 55`, `socialControl >= 55`, `directness <= 45`.',
    override: true
  },
  {
    typeId: 'hidden_read_receipt_ghost',
    zhShare: '已读鸽',
    zhDetail: '已读不回游离型',
    enShare: 'Seen Ghost',
    enDetail: 'Read-Receipt Drifter',
    trigger: 'Multiple leave-on-read / later / pretend-not-seen events; `avoidance >= 85`, `repairIntent <= 45`.',
    override: true
  },
  {
    typeId: 'hidden_group_chat_lawyer',
    zhShare: '群聊法官',
    zhDetail: '群聊规则裁判型',
    enShare: 'Chat Judge',
    enDetail: 'Group Chat Rule Lawyer',
    trigger: 'Multiple rule-checking / receipt-pulling / ask-for-explanation events; `directness >= 75`, `boundaryFirmness >= 75`, `socialControl >= 65`.',
    override: true
  },
  {
    typeId: 'hidden_meme_overlord',
    zhShare: '梗王',
    zhDetail: '极限玩梗统治者',
    enShare: 'Meme Lord',
    enDetail: 'Extreme Meme Defender',
    trigger: '`memeDefense >= 95`; at least 70% of key scenes choose meme/sticker/ironic but non-abusive options.',
    override: true
  },
  {
    typeId: 'hidden_midnight_replay',
    zhShare: '半夜悔',
    zhDetail: '夜间复盘型',
    enShare: 'Midnight Replay',
    enDetail: 'Late-Night Replayer',
    trigger: 'After high-conflict or high-emotion choices, repeatedly chooses regret/repair/replay paths; `emotionalVolatility >= 70`, `repairIntent >= 75`.',
    override: false
  },
  {
    typeId: 'hidden_plot_twister',
    zhShare: '剧情手',
    zhDetail: '混沌剧情推动者',
    enShare: 'Plot Twister',
    enDetail: 'Drama-Generating Catalyst',
    trigger: '`chaosAppetite >= 90`, `impulsiveness >= 70`; repeatedly chooses non-abusive watch-the-drama / add-one-line / make-it-interesting options.',
    override: true
  }
];

export const allPersonalityTypes: PersonalityType[] = [...regularTypes, ...hiddenTypes];

export function findPersonalityType(typeId: string): PersonalityType | undefined {
  return allPersonalityTypes.find((type) => type.typeId === typeId);
}
